import { useEffect, useState } from "react";
import { Lock, Eye, Users, TrendingUp, CalendarDays, Building2, User, LogOut, RefreshCw, Download, FileText, Share2 } from "lucide-react";

const partagerAffiche = async (label, file) => {
  const url = `${window.location.origin}/downloads/${file}`;
  const texte = `${label} — FK Conseil & Événement`;
  if (navigator.share) {
    try {
      await navigator.share({ title: "FK Conseil & Événement", text: texte, url });
      return;
    } catch (e) {
      /* annulé par l'utilisateur */
    }
  }
  window.open(`https://wa.me/?text=${encodeURIComponent(`${texte}\n${url}`)}`, "_blank", "noopener,noreferrer");
};

const AFFICHES_DL = [
  { id: "vip-blanc", label: "VIP 14 nov — Blanc Pur", file: "affiche-soiree-vip-14-novembre-blanc.png" },
  { id: "vip-bleu", label: "VIP 14 nov — Bleu Fête", file: "affiche-soiree-vip-14-novembre-bleu.png" },
  { id: "vip-rouge", label: "VIP 14 nov — Rouge Passion", file: "affiche-soiree-vip-14-novembre-rouge.png" },
  { id: "vip-or", label: "VIP 14 nov — Or Champagne", file: "affiche-soiree-vip-14-novembre-or.png" },
  { id: "vip-soleil", label: "VIP 14 nov — Soleil Party", file: "affiche-soiree-vip-14-novembre-soleil.png" },
  { id: "fk-editorial", label: "FK Public — Éditorial", file: "affiche-fk-evenement-public-editorial.png" },
  { id: "fk-couture", label: "FK Public — Duo Éclat", file: "affiche-fk-evenement-public-couture.png" },
  { id: "fk-duo", label: "FK Public — Service en duo", file: "affiche-fk-evenement-public-duo.png" },
  { id: "fk-duo-visages", label: "FK Public — Duo de serveurs", file: "affiche-fk-evenement-public-duo-visages.png" },
  { id: "fk-serveuse", label: "FK Public — Serveuse élégante", file: "affiche-fk-evenement-public-serveuse.png" },
  { id: "fk-artdeco", label: "FK Public — Art Déco", file: "affiche-fk-evenement-public-artdeco.png" },
  { id: "fk-architecte", label: "FK Pro — Architecte", file: "affiche-fk-evenement-pro-architecte.png" },
  { id: "fk-cinematique", label: "FK Pro — Cinématique", file: "affiche-fk-evenement-pro-cinematique.png" },
  { id: "fk-maitre", label: "FK Pro — Maître d'hôtel", file: "affiche-fk-evenement-pro-maitre.png" },
  { id: "carre-duo", label: "Instagram — Duo de serveurs", file: "affiche-fk-evenement-carre-duo.png" },
  { id: "carre-serveur", label: "Instagram — Serveur", file: "affiche-fk-evenement-carre-serveur.png" },
  { id: "carre-serveuse", label: "Instagram — Serveuse", file: "affiche-fk-evenement-carre-serveuse.png" },
  { id: "carre-maitre", label: "Instagram — Maître d'hôtel", file: "affiche-fk-evenement-carre-maitre.png" },
  { id: "carre-equipe", label: "Instagram — Brigade FK", file: "affiche-fk-evenement-carre-equipe.png" },
  { id: "carre-hotesse", label: "Instagram — Duo Éclat", file: "affiche-fk-evenement-carre-hotesse.png" },
  { id: "carre-salle", label: "Instagram — Salle Royale", file: "affiche-fk-evenement-carre-salle.png" },
];

const API = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-3xl border border-white/10 bg-[#0a0a0a]/60 p-6 backdrop-blur-xl" data-testid={testid}>
    <div className="flex items-center gap-3 text-gold">
      <Icon className="h-4 w-4" />
      <p className="text-[10px] font-semibold uppercase tracking-[0.3em]">{label}</p>
    </div>
    <p className="mt-4 font-display text-4xl text-white">{value}</p>
  </div>
);

export default function Espace() {
  const [token, setToken] = useState(() => localStorage.getItem("fk_espace_token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const loadStats = async (tok) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/espace/stats`, { headers: { "X-Espace-Token": tok } });
      if (res.status === 401) {
        localStorage.removeItem("fk_espace_token");
        setToken("");
        setData(null);
        setError("Session expirée, reconnectez-vous.");
        return;
      }
      if (!res.ok) throw new Error();
      setData(await res.json());
    } catch {
      setError("Erreur de chargement des statistiques.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadStats(token);
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/espace/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(typeof body.detail === "string" ? body.detail : "Mot de passe incorrect");
        return;
      }
      localStorage.setItem("fk_espace_token", body.token);
      setToken(body.token);
    } catch {
      setError("Erreur de connexion. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("fk_espace_token");
    setToken("");
    setData(null);
    setPassword("");
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#141414] px-6" data-testid="espace-page">
        <div className="w-full max-w-md">
          <a href="/" data-testid="espace-back-link" className="text-xs uppercase tracking-[0.3em] text-white/40 transition-colors hover:text-gold">
            ← Retour au site
          </a>
          <h1 className="mt-8 font-display text-4xl tracking-tight text-white sm:text-5xl">
            Espace <em className="italic text-gold">privé</em>
          </h1>
          <p className="mt-4 text-sm text-white/50">Statistiques des visiteurs et demandes de devis. Réservé au propriétaire.</p>
          <form onSubmit={handleLogin} className="mt-10 space-y-6 rounded-3xl border border-white/10 bg-[#0a0a0a]/60 p-8 backdrop-blur-xl" data-testid="espace-login-form">
            <div>
              <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-white/40">Mot de passe</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Votre mot de passe"
                data-testid="espace-password-input"
                className="w-full border-b border-white/20 bg-transparent py-4 font-display text-2xl text-white placeholder-white/25 outline-none transition-colors duration-300 focus:border-gold"
              />
            </div>
            {error && (
              <p className="text-sm text-red-400" data-testid="espace-login-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="espace-login-button"
              className="flex w-full items-center justify-center gap-3 rounded-full bg-gold py-5 text-xs font-bold uppercase tracking-[0.2em] text-[#0a0a0a] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-50"
            >
              <Lock className="h-4 w-4" />
              {loading ? "Connexion..." : "Accéder à mon espace"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const visits = data?.visits;
  const devis = data?.devis || [];

  return (
    <div className="min-h-screen bg-[#141414] px-6 py-20" data-testid="espace-dashboard">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <a href="/" data-testid="espace-back-link" className="text-xs uppercase tracking-[0.3em] text-white/40 transition-colors hover:text-gold">
            ← Retour au site
          </a>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => loadStats(token)}
              data-testid="espace-refresh-button"
              className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/70 transition-colors hover:border-gold hover:text-gold"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Actualiser
            </button>
            <button
              type="button"
              onClick={logout}
              data-testid="espace-logout-button"
              className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/70 transition-colors hover:border-gold hover:text-gold"
            >
              <LogOut className="h-3.5 w-3.5" />
              Quitter
            </button>
          </div>
        </div>

        <h1 className="mt-8 font-display text-4xl tracking-tight text-white sm:text-5xl">
          Votre <em className="italic text-gold">espace</em>
        </h1>
        <p className="mt-4 text-sm text-white/50">Visiteurs du site et demandes de devis reçues.</p>

        <div className="mt-10 rounded-3xl border border-gold/30 bg-[#0a0a0a]/60 p-6 backdrop-blur-xl" data-testid="espace-affiches">
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">Vos affiches publicitaires</p>
          <p className="mt-2 text-xs leading-relaxed text-white/50">
            Téléchargez vos affiches en un clic (image haute qualité, prête pour vos réseaux sociaux), ou ouvrez la page
            animée pour les voir en mouvement.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <a
              href="/affiche-vip"
              data-testid="espace-affiche-link"
              className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-[10px] font-bold uppercase tracking-[0.25em] text-[#0a0a0a] transition-transform duration-300 hover:scale-[1.03]"
            >
              Affiches animées — Soirée VIP
            </a>
            <a
              href="/affiches-fk"
              data-testid="espace-affiches-fk-link"
              className="inline-flex items-center gap-2 rounded-full border border-gold/60 px-6 py-3 text-[10px] font-bold uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:bg-gold hover:text-[#0a0a0a]"
            >
              Affiches animées — FK public &amp; pro
            </a>
          </div>
          <div className="mt-5 grid gap-2 sm:grid-cols-2">
            {AFFICHES_DL.map((a) => (
              <div
                key={a.id}
                data-testid={`espace-dl-${a.id}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-white/10 px-4 py-2.5 transition-colors hover:border-gold"
              >
                <span className="text-xs text-white/70">{a.label}</span>
                <span className="flex shrink-0 items-center gap-1.5">
                  <a
                    href={`/downloads/${a.file}`}
                    download
                    title="Télécharger en image PNG"
                    data-testid={`espace-dl-png-${a.id}`}
                    className="rounded-lg border border-white/15 p-2 text-white/60 transition-colors hover:border-gold hover:text-gold"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </a>
                  <a
                    href={`${API}/api/affiche-pdf/${a.file}`}
                    title="Télécharger en PDF"
                    data-testid={`espace-dl-pdf-${a.id}`}
                    className="rounded-lg border border-white/15 p-2 text-white/60 transition-colors hover:border-gold hover:text-gold"
                  >
                    <FileText className="h-3.5 w-3.5" />
                  </a>
                  <button
                    type="button"
                    onClick={() => partagerAffiche(a.label, a.file)}
                    title="Partager l'affiche"
                    data-testid={`espace-dl-share-${a.id}`}
                    className="rounded-lg border border-white/15 p-2 text-white/60 transition-colors hover:border-gold hover:text-gold"
                  >
                    <Share2 className="h-3.5 w-3.5" />
                  </button>
                </span>
              </div>
            ))}
          </div>
          <a
            href={`${API}/api/cartes-visite-pdf`}
            data-testid="espace-dl-cartes-visite"
            className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-gold/40 bg-gold/5 px-4 py-3 text-xs font-semibold text-gold transition-colors hover:bg-gold hover:text-[#0a0a0a]"
          >
            Cartes de visite — Planche A4 de 32 mini cartes noir &amp; or (traits de découpe inclus)
            <FileText className="h-3.5 w-3.5 shrink-0" />
          </a>
        </div>

        {loading && !data && <p className="mt-10 text-sm text-white/40" data-testid="espace-loading">Chargement...</p>}

        {visits && (
          <>
            <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="espace-stats-grid">
              <StatCard icon={Eye} label="Visites totales" value={visits.total} testid="espace-stat-total" />
              <StatCard icon={TrendingUp} label="Visites 7 jours" value={visits.last_7d} testid="espace-stat-7d" />
              <StatCard icon={Users} label="Visiteurs uniques" value={visits.unique} testid="espace-stat-unique" />
              <StatCard icon={CalendarDays} label="Uniques 7 jours" value={visits.unique_7d} testid="espace-stat-unique-7d" />
            </div>

            {visits.by_page?.length > 0 && (
              <div className="mt-6 rounded-3xl border border-white/10 bg-[#0a0a0a]/60 p-6 backdrop-blur-xl" data-testid="espace-by-page">
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">Pages visitées</p>
                <div className="mt-4 space-y-2">
                  {visits.by_page.map((p) => (
                    <div key={p.path} className="flex items-center justify-between text-sm">
                      <span className="text-white/60">{p.path}</span>
                      <span className="font-display text-white">{p.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <h2 className="mt-16 font-display text-2xl tracking-tight text-white">
              Demandes de devis <em className="italic text-gold">({devis.length})</em>
            </h2>
            <div className="mt-6 overflow-x-auto rounded-3xl border border-white/10 bg-[#0a0a0a]/60 backdrop-blur-xl" data-testid="espace-devis-table">
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40">
                    <th className="px-5 py-4">N°</th>
                    <th className="px-5 py-4">Client</th>
                    <th className="px-5 py-4">Catégorie</th>
                    <th className="px-5 py-4">Événement</th>
                    <th className="px-5 py-4">Coordonnées</th>
                    <th className="px-5 py-4 text-right">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {devis.map((d) => (
                    <tr key={d.numero} className="border-b border-white/5 transition-colors hover:bg-white/[0.03]" data-testid={`espace-devis-row-${d.numero}`}>
                      <td className="px-5 py-4 font-display text-white">{d.numero}</td>
                      <td className="px-5 py-4 text-white/80">{d.name}</td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] ${
                            d.categorie === "Entreprise" ? "bg-gold/15 text-gold" : "bg-white/10 text-white/70"
                          }`}
                          data-testid={`espace-categorie-${d.numero}`}
                        >
                          {d.categorie === "Entreprise" ? <Building2 className="h-3 w-3" /> : <User className="h-3 w-3" />}
                          {d.categorie}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-white/60">
                        {d.type}
                        <span className="block text-xs text-white/35">{d.date} — {d.place}</span>
                      </td>
                      <td className="px-5 py-4 text-white/60">
                        {d.email && <a href={`mailto:${d.email}`} className="block transition-colors hover:text-gold">{d.email}</a>}
                        {d.phone && (
                          <a href={`https://wa.me/${String(d.phone).replace(/\D/g, "").replace(/^0/, "33")}`} target="_blank" rel="noopener noreferrer" className="block transition-colors hover:text-gold">
                            {d.phone}
                          </a>
                        )}
                      </td>
                      <td className="px-5 py-4 text-right font-display text-gold">{d.montant} €</td>
                    </tr>
                  ))}
                  {devis.length === 0 && (
                    <tr>
                      <td colSpan="6" className="px-5 py-10 text-center text-white/40">Aucune demande pour le moment.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <h2 className="mt-16 font-display text-2xl tracking-tight text-white">
              Réservations soirée VIP <em className="italic text-gold">({(data?.reservations || []).length})</em>
            </h2>
            <p className="mt-2 text-xs text-white/40">Soirée du samedi 14 novembre — Factory58, Bobigny.</p>
            <div className="mt-6 overflow-x-auto rounded-3xl border border-white/10 bg-[#0a0a0a]/60 backdrop-blur-xl" data-testid="espace-reservations-table">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40">
                    <th className="px-5 py-4">N°</th>
                    <th className="px-5 py-4">Nom</th>
                    <th className="px-5 py-4">Catégorie</th>
                    <th className="px-5 py-4">Personnes</th>
                    <th className="px-5 py-4">Coordonnées</th>
                    <th className="px-5 py-4">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.reservations || []).map((r) => (
                    <tr key={r.numero} className="border-b border-white/5 transition-colors hover:bg-white/[0.03]" data-testid={`espace-reservation-row-${r.numero}`}>
                      <td className="px-5 py-4 font-display text-white">{r.numero}</td>
                      <td className="px-5 py-4 text-white/80">{r.name}</td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] ${
                            r.categorie === "VIP" ? "bg-gold/15 text-gold" : r.categorie === "Premium" ? "bg-white/15 text-white" : "bg-white/5 text-white/60"
                          }`}
                          data-testid={`espace-reservation-categorie-${r.numero}`}
                        >
                          {r.categorie}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-white/60">{r.personnes}</td>
                      <td className="px-5 py-4 text-white/60">
                        {r.email && <a href={`mailto:${r.email}`} className="block transition-colors hover:text-gold">{r.email}</a>}
                        {r.phone && (
                          <a href={`https://wa.me/${String(r.phone).replace(/\D/g, "").replace(/^0/, "33")}`} target="_blank" rel="noopener noreferrer" className="block transition-colors hover:text-gold">
                            {r.phone}
                          </a>
                        )}
                      </td>
                      <td className="max-w-[200px] px-5 py-4 text-white/50">{r.message || "—"}</td>
                    </tr>
                  ))}
                  {(data?.reservations || []).length === 0 && (
                    <tr>
                      <td colSpan="6" className="px-5 py-10 text-center text-white/40">Aucune réservation pour le moment.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
